import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import TimeInput from 'components/field/TimeInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Accordion from 'components/global/Accordion';
import Alert from 'components/global/Alert';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { Divider } from 'pages/OTOFlexTracking/OTOFlexTracking.styled';

import { AUTOCOMPLETE, ENUMS, SHIPPING_PARTNERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import * as S from './ConnectedPartners.styled';

const generateTimeSlots = (startTime, endTime, interval, cutoff, capacity) => {
	if (startTime && endTime && interval && cutoff && capacity) {
		const timeSlots = [];
		const today = dayjs();

		const [startHour, startMinute] = startTime.split(':');
		const [endHour, endMinute] = endTime.split(':');

		let start = dayjs().hour(startHour).minute(startMinute).second(0);
		const end = dayjs().hour(endHour).minute(endMinute).second(0);

		while (start < end) {
			const startTimestamp = start.valueOf();
			let endSlot = dayjs(startTimestamp + interval * 60000);
			endSlot = endSlot > end ? end : endSlot;
			const startStr = `${start.hour()}:${start.minute().toString().padStart(2, '0')}`;
			const endStr = `${endSlot.hour()}:${endSlot.minute().toString().padStart(2, '0')}`;
			const cutOffMs = startTimestamp - cutoff * 60000;
			const cutOffDate = dayjs(cutOffMs);
			const cutOffStr = `${cutOffDate.hour()}:${cutOffDate.minute().toString().padStart(2, '0')}`;
			const hasOverTime = dayjs(startTimestamp + interval * 60000) > end;
			timeSlots.push({
				startTime: start.toDate(),
				endTime: endSlot.toDate() > end.toDate() ? end.toDate() : endSlot.toDate(),
				slot: `${startStr} - ${endStr}`,
				cutOff: cutOffStr,
				capacity,
				date: today.toDate(),
				hasOverTime
			});
			start = endSlot;
		}

		return timeSlots;
	}
	return [];
};

const warehouseAutocompleteColumns = [{ accessor: 'Name', width: 200 }];

const formFieldOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const defaultColSpan = {
	md: 6
};

const DeliverySlotConfigurationForm = NiceModal.create(({ id, mode, modalName, deliveryCompanySettingsId }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { getLocaleDate } = useLocale();
	const { showError, showSuccess } = useAlert();
	const [tableData, setTableData] = useState([]);

	const { closeModal } = useModalRouter();

	const isViewMode = mode === 'view';

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					warehouseID: yup
						.number()
						.nullable()
						.when('branchID', ([branchID], schema) => (branchID ? schema : schema.required())),
					branchID: yup.lazy(() =>
						yup
							.number()
							.nullable()
							.when('warehouseID', ([warehouseID], schema) => (warehouseID ? schema : schema.required()))
					),
					pickupDaysOfWeek: yup
						.array()
						.nullable()
						.when('date', ([date], schema) => (date ? schema : schema.required())),
					date: yup.lazy(() =>
						yup
							.object()
							.nullable()
							.when(['pickupDaysOfWeek', 'date'], ([pickupDaysOfWeek, date], schema) =>
								pickupDaysOfWeek && !date?.start ? schema : schema.required()
							)
					),
					cutoffDuration: yup.number().min(0).required(),
					timeSlotInterval: yup.number().min(30).required(),
					capacity: yup.number().min(0).nullable().required(),
					startTime: yup.string().nullable().required(),
					endTime: yup.string().nullable().required()
				})
				.test('subscriptionDiscount', null, (value, { createError }) => {
					const { startTime, endTime } = value;
					const start = dayjs(startTime, 'HH:mm');
					const end = dayjs(endTime, 'HH:mm');

					if (start.isAfter(end) || start.isSame(end)) {
						return createError({
							path: 'endTime',
							message: 'endTime must be greater than startTime'
						});
					}
					return true;
				})
				.required(),

		[]
	);

	const fillForm = async () => {
		const defaultValues = {
			pickupDaysOfWeek: null,
			warehouseID: null,
			branchID: null,
			date: null,
			cutoffDuration: null
		};

		if (!id) {
			return defaultValues;
		}

		try {
			const { data, status } = await axiosPrivate.post(SHIPPING_PARTNERS.GET_DELIVERY_SLOT_METHOD, {
				deliverySlotGroupId: id
			});
			if (status === 200) {
				return {
					...defaultValues,
					...data,
					pickupDaysOfWeek: data.days ? data.days.split(',') : null,
					date: data.startDateInterval
						? data.endDateInterval && {
								start: data.startDateInterval,
								end: data.endDateInterval
							}
						: null
				};
			} else {
				throw new Error(status);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: async () => await fillForm()
	});

	const { handleSubmit, setValue, watch, clearErrors, formState, isLoading: isFormLoading } = formMethods;
	const { isDirty, isSubmitSuccessful, isSubmitting } = formState;
	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);
	const isLoading = isFormLoading || isSubmitting;

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const startTime = watch('startTime');
	const endTime = watch('endTime');
	const timeSlotInterval = watch('timeSlotInterval');
	const cutoffDuration = watch('cutoffDuration');
	const capacity = watch('capacity');
	const pickupDaysOfWeek = watch('pickupDaysOfWeek');
	const date = watch('date');

	useEffect(() => {
		if (formState.isValid) {
			setTableData(() => {
				let weekDaysDate = '';

				if (pickupDaysOfWeek) {
					weekDaysDate = pickupDaysOfWeek.join(', ');
				} else if (date) {
					const startDate = getLocaleDate(date.start);
					const endDate = date.end ? ` - ${getLocaleDate(date.end)}` : '';
					weekDaysDate = `${startDate}${endDate}`;
				}

				const slots = generateTimeSlots(startTime, endTime, timeSlotInterval, cutoffDuration, capacity);
				return [{ ...slots[0], weekDaysDate }, ...slots.slice(1)];
			});
		} else {
			setTableData([]);
		}
	}, [formState.isValid, startTime, endTime, timeSlotInterval, cutoffDuration, capacity, pickupDaysOfWeek, date]);

	const columns = useMemo(
		() => [
			{
				header: 'weekDaysDate',
				accessorKey: 'weekDaysDate',
				size: 120,
				minSize: 150,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<Typography fontSize="--paragraph3-font-size" fontWeight="500">
						{row.original.weekDaysDate}
					</Typography>
				)
			},
			{
				header: 'deliverySlot',
				accessorKey: 'slot',
				size: 30,
				minSize: 70,
				justifyContent: 'center',
				showOnMobile: true,
				renderCell: ({ row }) => (
					<Typography fontSize="--paragraph3-font-size" fontWeight="500">
						{row.original.slot}
					</Typography>
				)
			},
			{
				header: 'cutOffTime',
				accessorKey: 'cutOff',
				size: 30,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<Typography fontSize="--paragraph3-font-size" fontWeight="500">
						{row.original.cutOff}
					</Typography>
				)
			},
			{
				header: 'capacity',
				accessorKey: 'capacity',
				size: 30,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<Typography fontSize="--paragraph3-font-size" fontWeight="500">
						{row.original.capacity}
					</Typography>
				)
			}
		],
		[]
	);

	const onSubmit = async (data) => {
		const {
			capacity,
			startTime,
			endTime,
			warehouseID,
			branchID,
			cutoffDuration,
			timeSlotInterval,
			pickupDaysOfWeek,
			date
		} = data;

		const formatDate = (date) => dayjs(date).format('YYYY-MM-DD');

		const slots = generateTimeSlots(startTime, endTime, timeSlotInterval, cutoffDuration, capacity);
		await axiosPrivate
			.post(id ? SHIPPING_PARTNERS.EDIT_DELIVERY_SLOT_METHOD : SHIPPING_PARTNERS.ADD_DELIVERY_SLOT_METHOD, {
				deliverySlotGroupId: id ?? null,
				deliveryCompanySettingsId,
				ID: id ?? null,
				warehouseID,
				branchID,
				startTime,
				endTime,
				capacity,
				timeSlotInterval,
				cutoffDuration,
				days: pickupDaysOfWeek ? pickupDaysOfWeek?.join(',') : null,
				dateInterval:
					date?.start && date?.end
						? {
								start: formatDate(date.start),
								end: formatDate(date.end)
							}
						: date?.start
							? { start: formatDate(date.start) }
							: {},
				array: slots.map((slot) => ({
					slotStartTime: slot.startTime,
					slotEndTime: slot.endTime,
					capacity,
					date: slot.date,
					cutoffTime: cutoffDuration,
					warehouseID,
					branchID
				}))
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.DELIVERY_SLOT_MANAGEMENT));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				if (error.response?.data.code === 'error.OA001') {
					showError({
						message: 'error.thisCombinationAlreadyExists'
					});
				} else {
					AppLogger.error(error);
					showError();
				}
			});
	};

	const hasTimeWarning = useMemo(() => {
		const lastSlot = tableData?.[tableData?.length - 1];
		if (lastSlot) {
			return lastSlot.hasOverTime;
		}
		return false;
	}, [tableData]);

	const formTitle = useMemo(() => {
		switch (mode) {
			case 'edit':
				return 'editDeliverySlotManagement';
			case 'view':
				return 'viewDeliverySlotManagement';
			case 'add':
				return 'addDeliverySlotManagement';
			default:
				return '';
		}
	}, [mode]);

	return (
		<Modal fullwidth name={modalName} maxWidth="medium" className="modal-delivery-form">
			<ModalTitle>
				<Typography variant="p1">{formTitle}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless>
					<FormItem
						colSpan={defaultColSpan}
						label="warehouse"
						toolTip="warehouseTooltip"
						isLoading={isLoading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							value={watch('warehouseID')}
							name="warehouseID"
							disabled={!isViewMode && !!watch('branchID')}
							setValue={(value) => {
								setValue('warehouseID', value?.ID ?? null, formFieldOptions);
								if (value?.ID) {
									clearErrors('branchID');
									setValue('branchID', null, formFieldOptions);
								}
							}}
							viewMode={isViewMode}
						/>
					</FormItem>
					<FormItem label="branch" colSpan={defaultColSpan} isLoading={isLoading} toolTip="branchTooltip">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.BRANCHES}
							value={watch('branchID')}
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							name="branchID"
							disabled={!isViewMode && !!watch('warehouseID')}
							setValue={(value) => {
								setValue('branchID', value?.ID ?? null, formFieldOptions);
								if (value?.ID) {
									clearErrors('warehouseID');
									setValue('warehouseID', null, formFieldOptions);
								}
							}}
							viewMode={isViewMode}
						/>
					</FormItem>
					<FormItem
						colSpan={defaultColSpan}
						label="pickupDaysOfWeek"
						isLoading={isLoading}
						toolTip="pickUpDaysOfWeekTooltip">
						<EnumSelect
							searchable={false}
							multipleSelection
							name="pickupDaysOfWeek"
							enumName={ENUMS.DAY_OF_WEEK}
							disabled={!isViewMode && !!date}
							setValue={(value) => {
								setValue(`pickupDaysOfWeek`, value ?? null, formFieldOptions);
								clearErrors('date');
							}}
							value={pickupDaysOfWeek}
							viewMode={isViewMode}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="date" isLoading={isLoading} toolTip="dateTooltip">
						<DateInput
							name="date"
							rangeMode
							disabled={!isViewMode && !!pickupDaysOfWeek}
							value={date}
							setValue={(value) => {
								setValue('date', value || null, formFieldOptions);
								clearErrors('pickupDaysOfWeek');
							}}
							viewMode={isViewMode}
						/>
					</FormItem>
					<FormItem
						colSpan={defaultColSpan}
						label="startTime"
						isLoading={isLoading}
						toolTip="startTimeTooltip">
						<TimeInput
							name="startTime"
							setValue={(value) => {
								setValue('startTime', value ?? null, formFieldOptions);
							}}
							value={startTime}
							viewMode={isViewMode}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="endTime" isLoading={isLoading} toolTip="endTimeTooltip">
						<TimeInput
							name="endTime"
							setValue={(value) => {
								setValue('endTime', value ?? null, formFieldOptions);
							}}
							value={endTime}
							viewMode={isViewMode}
						/>
					</FormItem>
					<FormItem
						colSpan={defaultColSpan}
						label="timeSlotInterval"
						isLoading={isLoading}
						toolTip="timeSlotIntervalTooltip">
						<EnumSelect
							name="timeSlotInterval"
							enumName={ENUMS.TIME_SLOT_INTERVAL}
							setValue={(value) => {
								setValue('timeSlotInterval', value ?? null, formFieldOptions);
							}}
							value={timeSlotInterval}
							viewMode={isViewMode}
						/>
					</FormItem>

					<FormItem
						colSpan={defaultColSpan}
						label="cutOffTime"
						isLoading={isLoading}
						toolTip="cutOffTimeTooltip">
						<EnumSelect
							name="cutoffDuration"
							enumName={ENUMS.CUTOFF_TIME}
							setValue={(value) => {
								setValue('cutoffDuration', value ?? null, formFieldOptions);
							}}
							value={cutoffDuration}
							viewMode={isViewMode}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="capacity" isLoading={isLoading} toolTip="capacityTooltip">
						<IntegerInput name="capacity" showArrows={false} min={0} viewMode={isViewMode} />
					</FormItem>
				</Form>
				<S.SlotFormContainer>
					{hasTimeWarning && <Alert variant="warning" showIcon content="deliverySlotConfig.hasOverTime" />}
					<Divider />
					<Accordion header="preview" toolTip="previewTooltip">
						<Table columns={columns} data={tableData} />
					</Accordion>
				</S.SlotFormContainer>
			</ModalContent>

			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />

				<Button
					loading={isLoading}
					label={id ? 'save' : 'button.add'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});
export default DeliverySlotConfigurationForm;
